export interface Html5TrackerLike {
  destroy?: () => void;
  dispose?: () => void;
}

interface Logger {
  log: (...args: unknown[]) => void;
  error: (...args: unknown[]) => void;
}

export function getHtml5TrackerConstructor(
  moduleAny: Record<string, unknown>,
  logger: Logger,
  logPrefix = "[NR_HTML5_TRACKER]"
): null | (new (videoEl: HTMLVideoElement, config: Record<string, unknown>) => Html5TrackerLike) {
  const moduleDefault = moduleAny?.default as Record<string, unknown> | undefined;
  const candidate =
    moduleDefault?.default ??
    moduleDefault?.Html5Tracker ??
    moduleDefault ??
    moduleAny?.Html5Tracker ??
    moduleAny;

  if (typeof candidate === "function") {
    logger.log(logPrefix, "Html5Tracker constructor resolved");
    return candidate as new (
      videoEl: HTMLVideoElement,
      config: Record<string, unknown>
    ) => Html5TrackerLike;
  }

  logger.error(logPrefix, "Html5Tracker constructor not found", {
    moduleKeys: Object.keys(moduleAny || {}),
    defaultKeys: Object.keys(moduleDefault || {}),
    defaultType: typeof moduleDefault,
    html5TrackerType: typeof moduleAny?.Html5Tracker,
    defaultHtml5TrackerType: typeof moduleDefault?.Html5Tracker,
  });

  return null;
}

export function findVideoInShadowDOM(element: Element): HTMLVideoElement | null {
  const htmlElement = element as HTMLElement;

  // If the element itself is a video, return it immediately.
  if (element instanceof HTMLVideoElement) return element;

  // Search light-DOM descendants first (covers slotted or non-shadow implementations).
  try {
    const lightDomVideo = htmlElement.querySelector("video");
    if (lightDomVideo) return lightDomVideo as HTMLVideoElement;
  } catch {
    void 0;
  }

  // If element has shadowRoot, search there and nested shadow roots.
  const root = htmlElement.shadowRoot;
  if (root) {
    const directVideo = root.querySelector("video");
    if (directVideo) return directVideo as HTMLVideoElement;

    for (const child of Array.from(root.querySelectorAll("*"))) {
      const found = findVideoInShadowDOM(child);
      if (found) return found;
    }
  }

  // Also recurse into light-DOM children to discover nested shadow-roots.
  for (const child of Array.from(htmlElement.children)) {
    const found = findVideoInShadowDOM(child);
    if (found) return found;
  }

  return null;
}

export function buildHtml5TrackerSignature(params: {
  videoElement: HTMLVideoElement;
  contentTitle: string;
  videoId: string;
  liveStreamStatus: string;
}): string {
  const { videoElement, contentTitle, videoId, liveStreamStatus } = params;
  return `${videoElement.currentSrc ?? videoElement.src ?? "firework-stream"}::${contentTitle}::${videoId}::${liveStreamStatus}`;
}

export function disposeHtml5Tracker(
  tracker: Html5TrackerLike | null | undefined,
  logger: Logger,
  context: { reason: string; logPrefix?: string }
): void {
  if (!tracker) {
    return;
  }

  const logPrefix = context.logPrefix || "[NR_HTML5_TRACKER]";

  try {
    if (typeof tracker.destroy === "function") {
      logger.log(logPrefix, "Destroying Html5Tracker instance", {
        reason: context.reason,
        method: "destroy",
      });
      tracker.destroy();
      return;
    }

    if (typeof tracker.dispose === "function") {
      logger.log(logPrefix, "Destroying Html5Tracker instance", {
        reason: context.reason,
        method: "dispose",
      });
      tracker.dispose();
    }
  } catch (error) {
    logger.error(logPrefix, "Html5Tracker destroy failed", {
      reason: context.reason,
      error,
      errorMessage: error instanceof Error ? error.message : "unknown",
    });
  }
}
